import React, { useEffect, useState } from 'react';
import { ArrowRight, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from './ui/badge';

export default function AuthSplitLayout({
  badge,
  title,
  description,
  sideLabel,
  sideTitle,
  sideDescription,
  highlights = [],
  stats = [],
  dynamicPreview = false,
  children,
}) {
  const { t } = useTranslation();
  const [activeHighlight, setActiveHighlight] = useState(0);

  useEffect(() => {
    if (!dynamicPreview || highlights.length <= 1) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setActiveHighlight((current) => (current + 1) % highlights.length);
    }, 4200);

    return () => window.clearInterval(intervalId);
  }, [dynamicPreview, highlights.length]);

  const currentHighlight = highlights[activeHighlight] ?? highlights[0];
  const currentStat = stats[activeHighlight % Math.max(stats.length, 1)] ?? null;
  const CurrentHighlightIcon = currentHighlight?.icon ?? Sparkles;

  return (
    <div className="relative min-h-screen overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(125,211,252,0.34),_transparent_24%),linear-gradient(135deg,_#f7fbff_0%,_#eef6ff_42%,_#e6f0ff_100%)] lg:h-[100dvh] lg:min-h-0">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -left-16 top-20 h-64 w-64 rounded-full bg-white/55 blur-3xl" />
        <div className="absolute right-0 top-0 h-80 w-80 rounded-full bg-sky-200/35 blur-3xl" />
        <div className="absolute bottom-0 right-1/4 h-72 w-72 rounded-full bg-blue-200/25 blur-3xl" />
      </div>

      <div className="relative grid min-h-screen grid-cols-1 lg:h-[100dvh] lg:grid-cols-[minmax(460px,38vw)_1fr]">
        <section className="order-2 flex items-center px-4 pb-5 pt-3 sm:px-6 sm:pb-6 lg:order-1 lg:h-[100dvh] lg:px-5 lg:py-0 xl:px-10">
          <div className="w-full lg:max-h-[100dvh] lg:overflow-y-auto lg:pr-1">
            <div className="mx-auto max-w-[33rem] rounded-[30px] border border-white/70 bg-white/68 px-5 py-5 shadow-[0_28px_70px_rgba(148,163,184,0.14)] backdrop-blur-2xl lg:px-5 lg:py-5 xl:px-7 xl:py-7">
              <div className="flex flex-col gap-5 lg:gap-4 xl:gap-5">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-950 text-white shadow-lg shadow-slate-900/20 lg:h-9 lg:w-9">
                    <Sparkles className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500 lg:text-[11px]">Mailwise</p>
                    <p className="text-xs text-slate-600">{t('common.brandSubtitle')}</p>
                  </div>
                </div>

                <div className="space-y-3 lg:space-y-2">
                  <Badge
                    variant="outline"
                    className="w-fit rounded-full border-sky-200 bg-sky-50/85 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-sky-700 lg:px-2.5 lg:py-0.5"
                  >
                    {badge}
                  </Badge>
                  <div className="space-y-2 lg:space-y-1.5">
                    <h1 className="max-w-[13ch] text-4xl font-semibold tracking-[-0.05em] text-slate-950 sm:text-5xl lg:text-3xl lg:leading-tight xl:text-4xl xl:leading-tight">
                      {title}
                    </h1>
                    <p className="max-w-md text-sm leading-6 text-slate-600 lg:text-sm lg:leading-5 xl:text-base xl:leading-6">
                      {description}
                    </p>
                  </div>
                </div>

                {children}
              </div>
            </div>
          </div>
        </section>

        <aside className="hidden lg:order-2 lg:block lg:h-[100dvh] lg:px-0 lg:pt-0">
          {dynamicPreview ? (
            <div className="relative min-h-[45vh] overflow-hidden rounded-[30px] border border-white/70 bg-[linear-gradient(150deg,_rgba(255,255,255,0.82),_rgba(232,243,255,0.92)_52%,_rgba(214,231,255,0.92))] shadow-[0_30px_80px_rgba(125,154,204,0.18)] sm:px-8 lg:flex lg:h-[100dvh] lg:min-h-0 lg:items-center lg:rounded-none lg:border-y-0 lg:border-r-0 lg:px-8 xl:px-10">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(191,219,254,0.55),_transparent_28%),radial-gradient(circle_at_bottom_left,_rgba(255,255,255,0.75),_transparent_38%),linear-gradient(180deg,_rgba(255,255,255,0.06),_rgba(255,255,255,0.18))]" />
              <div className="absolute left-10 top-10 h-20 w-20 rounded-full border border-white/65 bg-white/30" />
              <div className="absolute bottom-12 right-12 h-14 w-14 rounded-[1.6rem] bg-white/38 backdrop-blur-xl" />
              <div className="absolute left-1/3 top-1/2 h-72 w-72 -translate-y-1/2 rounded-full bg-white/40 blur-3xl" />

              <div className="relative flex w-full flex-col gap-5 px-6 py-8 lg:mx-auto lg:max-w-[660px] lg:justify-center lg:px-0 lg:py-0 xl:max-w-[700px] xl:gap-6">
                <div className="space-y-2.5 lg:space-y-2">
                  <Badge className="rounded-full border border-sky-200 bg-white/82 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-sky-700 shadow-sm hover:bg-white/82 lg:px-2.5 lg:py-0.5">
                    {sideLabel}
                  </Badge>
                  <div className="space-y-3 lg:space-y-2">
                    <h2 className="max-w-[12ch] text-4xl font-semibold tracking-[-0.05em] text-slate-950 sm:text-5xl lg:text-3xl lg:leading-tight xl:text-4xl xl:leading-tight">
                      {sideTitle}
                    </h2>
                    <p className="max-w-xl text-sm leading-6 text-slate-600 lg:text-sm lg:leading-5 xl:text-base xl:leading-6">
                      {sideDescription}
                    </p>
                  </div>
                </div>

                <div className="relative flex items-center justify-center py-2 lg:py-1 xl:py-4">
                  {stats.slice(0, 3).map((stat, index) => (
                    <div
                      key={stat.label}
                      className={`absolute hidden rounded-[20px] border border-white/80 bg-white/74 p-2.5 text-slate-900 shadow-[0_14px_34px_rgba(148,163,184,0.16)] backdrop-blur-xl xl:block ${
                        index === 0 ? '-left-1 top-10 w-28 -rotate-6' : ''
                      } ${index === 1 ? 'right-0 top-20 w-28 rotate-6' : ''} ${
                        index === 2 ? 'bottom-10 left-6 w-32 rotate-[-4deg]' : ''
                      } ${activeHighlight % Math.max(stats.length, 1) === index ? 'scale-105 shadow-[0_24px_52px_rgba(125,154,204,0.24)]' : ''}`}
                    >
                      <p className="text-base font-semibold">{stat.value}</p>
                      <p className="mt-1 text-[11px] uppercase tracking-[0.18em] text-slate-500">{stat.label}</p>
                    </div>
                  ))}

                  <div className="relative w-full max-w-[360px] rounded-[26px] border border-white/85 bg-white/76 p-4 text-center shadow-[0_24px_60px_rgba(148,163,184,0.18)] backdrop-blur-2xl sm:max-w-[390px] sm:p-5 xl:max-w-[440px] xl:p-6">
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-[18px] bg-[linear-gradient(180deg,_#dbeafe,_#bfdbfe)] text-sky-700 shadow-[inset_0_1px_0_rgba(255,255,255,0.7),0_18px_35px_rgba(147,197,253,0.35)] xl:h-14 xl:w-14 xl:rounded-[20px]">
                      <CurrentHighlightIcon className="h-5 w-5 xl:h-6 xl:w-6" />
                    </div>

                    <div key={currentHighlight?.title ?? 'fallback'} className="mt-3 space-y-2.5 transition-all duration-500 xl:mt-4 xl:space-y-3">
                      <div className="space-y-1.5">
                        <p className="text-2xl font-semibold tracking-[-0.05em] text-slate-950 lg:text-[1.7rem] xl:text-3xl">
                          {currentHighlight?.title ?? sideTitle}
                        </p>
                        <p className="mx-auto max-w-sm text-xs leading-5 text-slate-500 sm:text-sm xl:text-sm xl:leading-6">
                          {currentHighlight?.description ?? sideDescription}
                        </p>
                      </div>

                      {currentStat && (
                        <div className="mx-auto flex w-fit items-center gap-2 rounded-full border border-sky-100 bg-sky-50/85 px-2.5 py-1 text-left shadow-sm xl:px-3 xl:py-1.5">
                          <div className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse xl:h-2 xl:w-2" />
                          <div>
                            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                              {currentStat.label}
                            </p>
                            <p className="text-xs font-semibold text-slate-900">{currentStat.value}</p>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="mt-4 flex items-center justify-center gap-1 xl:mt-5 xl:gap-1.5">
                      {highlights.map((item, index) => (
                        <button
                          key={item.title}
                          type="button"
                          onClick={() => setActiveHighlight(index)}
                          className={`rounded-full transition-all duration-500 ${
                            index === activeHighlight ? 'h-1.5 w-6 bg-slate-900 xl:h-2 xl:w-6' : 'h-1.5 w-1.5 bg-slate-300 hover:bg-slate-400 xl:h-2 xl:w-2'
                          }`}
                          aria-label={item.title}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                  {stats.slice(0, 3).map((stat, index) => (
                    <div
                      key={stat.label}
                      className={`rounded-[20px] border border-white/75 bg-white/58 p-2.5 backdrop-blur-xl transition-all duration-500 xl:hidden ${
                        activeHighlight % Math.max(stats.length, 1) === index ? 'shadow-[0_12px_28px_rgba(125,154,204,0.12)]' : 'shadow-[0_8px_20px_rgba(148,163,184,0.08)]'
                      }`}
                    >
                      <p className="text-sm font-semibold text-slate-950">{stat.value}</p>
                      <p className="mt-1 text-[11px] uppercase tracking-[0.16em] text-slate-500">{stat.label}</p>
                    </div>
                  ))}
                </div>

              </div>
            </div>
          ) : (
            <div className="relative min-h-[45vh] overflow-hidden rounded-[30px] border border-slate-200/70 bg-slate-950 px-6 py-8 text-white shadow-[0_35px_100px_rgba(15,23,42,0.28)] sm:px-8 lg:flex lg:h-[100dvh] lg:min-h-0 lg:items-center lg:rounded-none lg:border-y-0 lg:border-r-0 lg:px-12 lg:py-8">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(56,189,248,0.34),_transparent_25%),radial-gradient(circle_at_bottom_left,_rgba(59,130,246,0.22),_transparent_35%),linear-gradient(160deg,_rgba(2,6,23,0.96),_rgba(15,23,42,0.98))]" />
              <div className="absolute right-10 top-10 h-24 w-24 rounded-full border border-white/10" />
              <div className="absolute bottom-16 left-8 h-20 w-20 rounded-3xl bg-white/5" />

              <div className="relative mx-auto flex w-full max-w-[760px] flex-col justify-center gap-6">
                <div className="space-y-4">
                  <Badge className="rounded-full bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-sky-100 hover:bg-white/10">
                    {sideLabel}
                  </Badge>
                  <div className="space-y-3">
                    <h2 className="max-w-xl text-3xl font-semibold tracking-[-0.04em] text-white sm:text-5xl">
                      {sideTitle}
                    </h2>
                    <p className="max-w-xl text-sm leading-7 text-slate-300 sm:text-base">
                      {sideDescription}
                    </p>
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-3">
                  {stats.map((stat) => (
                    <div key={stat.label} className="rounded-2xl border border-white/10 bg-white/6 p-4 backdrop-blur-sm">
                      <p className="text-2xl font-semibold text-white">{stat.value}</p>
                      <p className="mt-1 text-xs uppercase tracking-[0.18em] text-slate-300">{stat.label}</p>
                    </div>
                  ))}
                </div>

                <div className="grid gap-3">
                  {highlights.map((item) => {
                    const Icon = item.icon;
                    return (
                      <div key={item.title} className="flex items-start gap-4 rounded-2xl border border-white/10 bg-white/6 p-4 backdrop-blur-sm">
                        <div className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-sky-400/15 text-sky-200">
                          <Icon className="h-5 w-5" />
                        </div>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-white">{item.title}</p>
                            <ArrowRight className="h-4 w-4 text-sky-200" />
                          </div>
                          <p className="text-sm leading-6 text-slate-300">{item.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>

              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
